import React from "react"
import { isShipmentDeleted, shipmentDeletedRowClass } from "../../utils/shipmentDeleted.js"
import { shipmentHasUnremittedPaid } from "../../utils/mlPaidSettlementRules.js"

const formatShipmentMoney = (amount) => {
  const num = Number(amount)
  if (!Number.isFinite(num)) return "—"
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const formatShipmentBalance = (amount) => formatShipmentMoney(amount)

const formatShipmentPaymentMode = (mode) => {
  const key = String(mode || "").toLowerCase().replace(/\s+/g, "_")
  if (key === "to_pay") return "To Pay"
  if (key === "paid") return "Paid"
  if (key === "credit" || key === "on_account") return "Credit"
  if (key === "paid_balance") return "Paid Balance"
  return mode || "—"
}

const getShipmentPackageCount = (shipment) => {
  const count = Number(
    shipment?.no_of_articles
      ?? shipment?.packages
      ?? shipment?.total_packages
      ?? shipment?.article_count
      ?? 0,
  )
  return Number.isFinite(count) ? count : 0
}

const PartyCell = ({ name, contact, location }) => (
  <div className="min-w-0">
    <div className="font-semibold text-gray-800 break-words leading-snug">{name || "—"}</div>
    {contact ? <div className="text-xs text-gray-500 mt-0.5 break-all">{contact}</div> : null}
    {location ? <div className="text-xs text-gray-400 mt-0.5 break-words">{location}</div> : null}
  </div>
)

const PartyCard = ({ label, name, contact, location }) => (
  <div className="min-w-0 rounded-lg bg-gray-50 border border-gray-100 p-2 sm:p-2.5">
    <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{label}</p>
    <p className="text-xs sm:text-sm font-semibold text-gray-800 break-words leading-snug">{name || "—"}</p>
    {contact ? <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 break-all">{contact}</p> : null}
    {location ? <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 break-words">{location}</p> : null}
  </div>
)

/**
 * Shared shipment picker for Add / Edit ML sheet.
 * - Mobile + iPad: card layout (xl:hidden)
 * - Desktop: full table (hidden xl:block)
 * Clicking anywhere on a row/card toggles selection (except View / blocked).
 */
const MLSheetShipmentRecordList = ({
  shipments = [],
  selectedShipmentIds = [],
  onToggle,
  onSelectAll,
  selectAllChecked = false,
  onView,
  getStatusColor,
  emptyMessage = "No shipments found",
  isShipmentDisabled = (shipment) =>
    shipmentHasUnremittedPaid(shipment) || isShipmentDeleted(shipment),
}) => {
  if (!shipments.length) {
    return (
      <div className="text-center py-8 sm:py-12 px-3">
        <p className="text-gray-500 text-sm sm:text-base">{emptyMessage}</p>
      </div>
    )
  }

  const statusClass = (status) =>
    getStatusColor ? getStatusColor(status) : "bg-gray-100 text-gray-700"

  const toggleIfAllowed = (shipment, shipmentId) => {
    if (isShipmentDisabled(shipment)) return
    onToggle?.(shipmentId)
  }

  return (
    <>
      {/* Desktop table — wide screens only (avoids cramped / sideways scroll on iPad) */}
      <div className="hidden xl:block w-full max-w-full overflow-x-auto overscroll-x-contain">
        <table className="w-full min-w-[980px] border-collapse">
          <thead>
            <tr className="bg-gray-50 text-gray-700 border-b">
              <th className="px-3 py-3 text-center w-12 sticky left-0 bg-gray-50 z-[1]">
                <input
                  type="checkbox"
                  checked={selectAllChecked}
                  onChange={onSelectAll}
                  className="w-4 h-4 accent-blue-500"
                  aria-label="Select all shipments"
                />
              </th>
              <th className="px-3 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">Shipment ID</th>
              <th className="px-3 py-3 text-left text-xs lg:text-sm font-semibold min-w-[150px]">Sender</th>
              <th className="px-3 py-3 text-left text-xs lg:text-sm font-semibold min-w-[150px]">Receiver</th>
              <th className="px-3 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">Payment</th>
              <th className="px-3 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">Packages</th>
              <th className="px-3 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">Amount</th>
              <th className="px-3 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">Balance</th>
              <th className="px-3 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">Status</th>
              {onView ? (
                <th className="px-3 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">Action</th>
              ) : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {shipments.map((shipment, index) => {
              const shipmentId = shipment.shipment_id || shipment.id
              const isSelected = selectedShipmentIds.includes(shipmentId)
              const isMinimal = Boolean(shipment.isMinimal)
              const selectionBlocked = isShipmentDisabled(shipment)

              return (
                <tr
                  key={shipmentId}
                  role="button"
                  tabIndex={selectionBlocked ? -1 : 0}
                  onClick={() => toggleIfAllowed(shipment, shipmentId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      toggleIfAllowed(shipment, shipmentId)
                    }
                  }}
                  className={shipmentDeletedRowClass(
                    shipment,
                    `hover:bg-gray-50 transition-colors ${
                      selectionBlocked ? "cursor-not-allowed" : "cursor-pointer"
                    } ${index % 2 === 0 ? "bg-white" : "bg-gray-50/60"} ${
                      isSelected ? "ring-1 ring-inset ring-orange-200" : ""
                    } ${selectionBlocked ? "opacity-60" : ""}`,
                  )}
                >
                  <td className="px-3 py-3 text-center sticky left-0 bg-inherit z-[1]">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={selectionBlocked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleIfAllowed(shipment, shipmentId)}
                      className="w-4 h-4 accent-blue-500"
                      aria-label={`Select ${shipmentId}`}
                    />
                  </td>
                  <td className="px-3 py-3 text-sm whitespace-nowrap align-top">
                    <div className="font-semibold text-secondary">{shipmentId}</div>
                    {shipment.consignment_no ? (
                      <div className="text-[11px] text-gray-400 mt-0.5">CN: {shipment.consignment_no}</div>
                    ) : null}
                    {selectionBlocked ? (
                      <div className="text-[11px] text-red-600 mt-1">Settle paid amount first</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-3 text-sm align-top">
                    {!isMinimal ? (
                      <PartyCell
                        name={shipment.sender_name || shipment.consigner_name}
                        contact={shipment.sender_contact || shipment.consigner_contact}
                        location={shipment.sender_location || shipment.consigner_location}
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-sm align-top">
                    {!isMinimal ? (
                      <PartyCell
                        name={shipment.receiver_name || shipment.consignee_name}
                        contact={shipment.receiver_contact || shipment.consignee_contact}
                        location={shipment.receiver_location || shipment.consignee_location}
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-sm capitalize whitespace-nowrap align-top">
                    {!isMinimal ? formatShipmentPaymentMode(shipment.mode_of_payment) : "—"}
                  </td>
                  <td className="px-3 py-3 text-sm font-semibold text-gray-800 whitespace-nowrap align-top">
                    {!isMinimal ? getShipmentPackageCount(shipment) : "—"}
                  </td>
                  <td className="px-3 py-3 text-sm font-semibold text-gray-800 whitespace-nowrap align-top">
                    {!isMinimal ? formatShipmentMoney(shipment.total_amount) : "—"}
                  </td>
                  <td className="px-3 py-3 text-sm font-semibold whitespace-nowrap align-top">
                    {!isMinimal ? (
                      <span
                        className={
                          Number(shipment.balance_amount) > 0 ? "text-red-700" : "text-green-700"
                        }
                      >
                        {formatShipmentBalance(shipment.balance_amount)}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-3 text-sm whitespace-nowrap align-top">
                    {!isMinimal && shipment.status ? (
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] lg:text-xs font-bold uppercase ${statusClass(
                          shipment.status,
                        )}`}
                      >
                        {shipment.status}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  {onView ? (
                    <td className="px-3 py-3 text-center align-top" onClick={(e) => e.stopPropagation()}>
                      {!isMinimal ? (
                        <button
                          type="button"
                          onClick={() => onView(shipment)}
                          className="text-orange-600 hover:text-orange-700 font-semibold hover:underline"
                        >
                          View →
                        </button>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile + iPad cards */}
      <div className="xl:hidden space-y-2 sm:space-y-3">
        {shipments.map((shipment) => {
          const shipmentId = shipment.shipment_id || shipment.id
          const isSelected = selectedShipmentIds.includes(shipmentId)
          const isMinimal = Boolean(shipment.isMinimal)
          const balanceNum = Number(shipment.balance_amount)
          const selectionBlocked = isShipmentDisabled(shipment)

          return (
            <div
              key={shipmentId}
              role="button"
              tabIndex={selectionBlocked ? -1 : 0}
              onClick={() => toggleIfAllowed(shipment, shipmentId)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  toggleIfAllowed(shipment, shipmentId)
                }
              }}
              className={shipmentDeletedRowClass(
                shipment,
                `rounded-xl border border-l-4 bg-white shadow-sm p-3 sm:p-4 transition-colors min-w-0 ${
                  selectionBlocked ? "cursor-not-allowed" : "cursor-pointer"
                } ${
                  isSelected
                    ? "border-orange-300 border-l-orange-500 bg-orange-50/40"
                    : "border-gray-100 border-l-orange-400"
                } ${selectionBlocked ? "opacity-60" : ""}`,
              )}
            >
              <div className="flex items-start gap-2.5 sm:gap-3 mb-2.5 sm:mb-3">
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={selectionBlocked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleIfAllowed(shipment, shipmentId)}
                  className="w-5 h-5 sm:w-[1.15rem] sm:h-[1.15rem] mt-0.5 shrink-0 accent-blue-500"
                  aria-label={`Select ${shipmentId}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-start justify-between gap-1.5 sm:gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-orange-600 text-sm sm:text-base break-all leading-snug">
                        {shipmentId}
                      </div>
                      {shipment.consignment_no ? (
                        <div className="text-[10px] sm:text-xs text-gray-400 mt-0.5">
                          CN: {shipment.consignment_no}
                        </div>
                      ) : null}
                      {selectionBlocked ? (
                        <div className="text-[10px] sm:text-xs text-red-600 mt-1">
                          Settle paid amount first
                        </div>
                      ) : null}
                    </div>
                    {!isMinimal && shipment.status ? (
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold uppercase shrink-0 ${statusClass(
                          shipment.status,
                        )}`}
                      >
                        {shipment.status}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              {!isMinimal ? (
                <>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-2.5 sm:mb-3">
                    <PartyCard
                      label="Sender"
                      name={shipment.sender_name || shipment.consigner_name}
                      contact={shipment.sender_contact || shipment.consigner_contact}
                      location={shipment.sender_location || shipment.consigner_location}
                    />
                    <PartyCard
                      label="Receiver"
                      name={shipment.receiver_name || shipment.consignee_name}
                      contact={shipment.receiver_contact || shipment.consignee_contact}
                      location={shipment.receiver_location || shipment.consignee_location}
                    />
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 text-xs sm:text-sm pt-2.5 sm:pt-3 border-t border-gray-200">
                    <div className="min-w-0">
                      <span className="text-gray-500 block">Payment</span>
                      <p className="font-semibold text-gray-800 capitalize break-words">
                        {formatShipmentPaymentMode(shipment.mode_of_payment)}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <span className="text-gray-500 block">Packages</span>
                      <p className="font-semibold text-gray-800">{getShipmentPackageCount(shipment)}</p>
                    </div>
                    <div className="min-w-0">
                      <span className="text-gray-500 block">Amount</span>
                      <p className="font-semibold text-gray-800 break-words">
                        {formatShipmentMoney(shipment.total_amount)}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <span className="text-gray-500 block">Balance</span>
                      <p
                        className={`font-semibold break-words ${
                          balanceNum > 0 ? "text-red-700" : "text-green-700"
                        }`}
                      >
                        {formatShipmentBalance(shipment.balance_amount)}
                      </p>
                    </div>
                  </div>
                </>
              ) : null}

              {onView && !isMinimal ? (
                <div className="mt-2.5 pt-2 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => onView(shipment)}
                    className="text-sm font-semibold text-orange-600 hover:text-orange-700"
                  >
                    View shipment →
                  </button>
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </>
  )
}

export default MLSheetShipmentRecordList
